import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../Context/ThemeContext';
import { getDailyTaxReport, getMachineLocations, getSingleFranchise, getTaxDetails, getTaxReportPDF } from '../../Services/authService';
import TransactionsTable from '../Views/Transcations/TransactionsTable';
import { Box, Button, TablePagination, Tooltip } from '@mui/material';
import { DARKCOLORS, LIGHTCOLORS } from '../../Utils/constants';
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import SuccessNotification from '../../Components/Common/SuccessNotification.js';

function TaxTableReport({ month, selectedLocations, dateRange, applyClicked, cardData }) {
    const { mode } = useThemeContext();
    const { t } = useTranslation();

    const backgroundCard = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
    const buttonBackgroundColor = mode === 'dark' ? DARKCOLORS.Secondary500 : LIGHTCOLORS.Secondary500;
    const borderColor1 = mode === 'dark' ? DARKCOLORS.BaseBlack : LIGHTCOLORS.BaseBlack;

    const columns = [
        { field: "machineNumber", label: t("serviceRequest.machineNumber") },
        { field: "transaction_date", label: t("transactions.transactionDate") },
        { field: "city", label: t("taxes.city") },
        { field: "location", label: t("transactions.location") },
        // { field: "productSummary", label: t("taxes.itemsSold") },
        {
            field: "productSummary",
            label: t("taxes.itemsSold"),
            renderCell: (row) => (
                <Tooltip title={row.productSummary || ''} arrow>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: '100%' }}>
                        {row.productSummary}
                    </span>
                </Tooltip>
            )
        },
        { field: "grossAmount", label: t("taxes.gross") },
        { field: "netAmount", label: t("taxes.net") },
        // { field: "vatPercentage", label: `${t("taxes.vat")} %` },
        { field: "vatAmount", label: `${t("taxes.vatAmount")} (7%)` }
    ];

    const [rows, setRows] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [machineIds, setMachineIds] = useState([])
    const [showTaxWarning, setShowTaxWarning] = useState(false);

    const fetchLocations = async () => {
        try {
            const data = await getMachineLocations(); // Fetch locations from backend    
            return data.flatMap(city => city.machines.map(machine => machine.machineId));
        } catch (error) {
            console.error(t("taxes.errorFetchingLocations"), error);
            return []; // Return an empty array if fetching fails
        }
    };

    const fetchTaxReportData = async () => {
        setLoading(true);
        setError(null);
        try {
            if (month !== "Default Month") {
                const formatLocalDate = (date) => {
                    if (!date) return null;
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
                    const day = String(date.getDate()).padStart(2, "0");
                    return `${year}-${month}-${day}`; // Returns YYYY-MM-DD
                };
                let locationsToSend = selectedLocations;
                if (!selectedLocations || selectedLocations.length === 0) {
                    locationsToSend = await fetchLocations(); // Fetch and use locations directly
                }
                const startDate = dateRange[0]?.startDate ? formatLocalDate(new Date(dateRange[0].startDate)) : null;
                const endDate = dateRange[0]?.endDate ? formatLocalDate(new Date(dateRange[0].endDate)) : null;
                const data = await getDailyTaxReport(page, rowsPerPage, month, startDate, endDate, locationsToSend);
                setRows(data?.transactions);
                setTotalRows(data?.totalRecords);
            } else {
                setRows(null); setTotalRows(null)
            }
        } catch (err) {
            setError(t('taxes.Failedloaddata'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTaxReportData();
    }, [page, rowsPerPage, month, selectedLocations, applyClicked]);

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handle individual row selection
    const handleRowSelect = (row) => {
        if (selectedRows.some((selectedRow) => selectedRow.id === row.id)) {
            setSelectedRows(selectedRows.filter((selectedRow) => selectedRow.id !== row.id));
        } else {
            setSelectedRows([...selectedRows, row]);
        }
    };

    // Handle "Select All"
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedRows([]); // Deselect all
        } else {
            setSelectedRows(rows); // Select all
        }
        setSelectAll(!selectAll);
    };

    const fetchData = async () => {
        // setLoading(true);
        try {
            const data = await getSingleFranchise();
            return data
        } catch (error) {
            console.error(t('taxes.errorFetchingFranchiseDetails'), error);
            // setGeneralError('Failed to load franchise details.');
        }
    };

    const handleExport = async () => {
        const franchiseData = await fetchData();

        if (!franchiseData?.taxIdNumber) {
            setShowTaxWarning(true); // Show the snackbar
            return;
        }

        setShowTaxWarning(false); // Clear warning if previously shown
        await getTaxReportPDF(
    franchiseData.id,
    month,
    t("taxes.export_file_name", "Tax_Report")
);
    };

    return (
        <Box style={{ background: backgroundCard, borderRadius: "12px", marginTop: "20px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 2 }}>
                <Box></Box>
                {/* Export Btn */}
                <Button style={{
                    fontSize: "13px", margin: "12px", background: buttonBackgroundColor, color: borderColor1,
                    padding: "9.5px 26px", textTransform: "none", borderRadius: "24px"
                }} onClick={handleExport} endIcon={<FileUploadOutlinedIcon />}>
                    {t('membershipCards.export')}
                </Button>
            </Box>

            {loading ? (
                <p>{t('vendingMachines.Loading')}</p>
            ) : (
                <>
                    <TransactionsTable
                        rows={rows || []}
                        columns={columns || []}
                        onRowSelect={handleRowSelect}
                        onSelectAll={handleSelectAll}
                        selectedRows={selectedRows || []}
                        selectAll={selectAll}
                    />
                    <TablePagination
                        component="div"
                        count={totalRows}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center', // Center the pagination controls
                            alignItems: 'center',
                        }}
                    />
                </>
            )
            }
            <SuccessNotification
                open={showTaxWarning}
                message={t('taxes.messagePDF')}
                severity="warning"
                onClose={() => setShowTaxWarning(false)}
            />

        </Box >
    )
}

export default TaxTableReport